<template>
  <div class="import-map-wrap">
    <div ref="container" class="import-map" aria-hidden="true"></div>
    <!-- Snap to paths: which line is which. -->
    <div
      v-if="model.legend !== 'hidden'"
      class="import-map-legend"
      :class="{ 'import-map-legend--matching': model.legend === 'matching' }"
      data-test="snap-legend"
      role="note"
    >
      <template v-if="model.legend === 'matching'">
        <span class="import-map-legend__spinner" aria-hidden="true"></span>
        <span data-test="snap-legend-matching">{{ $t('import_files.snap_to_paths_progress') }}</span>
      </template>
      <template v-else>
        <span class="import-map-legend__row" data-test="snap-legend-snapped">
          <span class="import-map-legend__swatch import-map-legend__swatch--snapped" aria-hidden="true"></span>
          {{ $t('import_files.snap_to_paths_legend_snapped') }}
        </span>
        <span class="import-map-legend__row" data-test="snap-legend-original">
          <span class="import-map-legend__swatch import-map-legend__swatch--original" aria-hidden="true"></span>
          {{ $t('import_files.snap_to_paths_legend_original') }}
        </span>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import mapboxgl from 'mapbox-gl';
import { HiikerMapStyle } from '../../utils/models/MapStyles';
import { boundsOf, cameraPoints, type ImportMapModel } from '../../utils/import/importMapModel';

// The Import files preview: the app's standard map, not interactive. Every line is red and
// bold with a white casing, a left-out file's line is dashed, kept waypoints are the
// Waypoints map's own "other" marker, a peak CSV's matched peaks are yellow (to bag) or grey
// (already bagged), only the open file is drawn while one is open, and the camera eases to the
// open file and back out when it closes. A snapped day's original line is grey and dashed under
// its red snapped line, with a legend saying which is which. What to draw comes from
// importMapModel.ts; this only draws it.
const props = defineProps<{ model: ImportMapModel }>();

const MAPBOX_TOKEN = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w';
const PIN_ICON = 'custom_waypoint_icon_other';
const RED = '#DB2B35';
const PEAK_TO_BAG = '#FDDF00';
const PEAK_BAGGED = '#ACB0BF';
/** The line as the file drew it, under its snapped version. */
const ORIGINAL_GREY = '#9AA0A6';
/** A lone waypoint or a very short line shouldn't zoom in to street level. */
const MAX_ZOOM = 15;
/** How long the camera takes to move to a file that's just been opened. */
const FOCUS_EASE_MS = 500;

const container = ref<HTMLDivElement | null>(null);
let map: mapboxgl.Map | null = null;
let loaded = false;
let framedOn: string | null = null;
let resizeObserver: ResizeObserver | null = null;

const linesData = (m: ImportMapModel) => ({
  type: 'FeatureCollection',
  features: m.lines.map(l => ({
    type: 'Feature',
    properties: { dashed: l.dashed, original: !!l.original },
    geometry: { type: 'LineString', coordinates: l.points.map(p => [p.lon, p.lat]) }
  }))
});

const peaksData = (m: ImportMapModel) => ({
  type: 'FeatureCollection',
  features: m.peaks.map(p => ({
    type: 'Feature',
    properties: { toBag: p.toBag },
    geometry: { type: 'Point', coordinates: [p.point.lon, p.point.lat] }
  }))
});

const pinsData = (m: ImportMapModel) => ({
  type: 'FeatureCollection',
  features: m.pins.map(p => ({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [p.lon, p.lat] } }))
});

function addLayers(m: mapboxgl.Map): void {
  m.addSource('import-lines', { type: 'geojson', data: linesData(props.model) as any });
  m.addSource('import-pins', { type: 'geojson', data: pinsData(props.model) as any });
  m.addSource('import-peaks', { type: 'geojson', data: peaksData(props.model) as any });
  // Under the snapped line: the red line is what imports, so it's the one on top; the grey shows
  // wherever the snapping moved the line off the original.
  m.addLayer({
    id: 'import-lines-original',
    type: 'line',
    source: 'import-lines',
    filter: ['get', 'original'],
    layout: { 'line-join': 'round' },
    paint: { 'line-color': ORIGINAL_GREY, 'line-width': 3, 'line-dasharray': [2, 1.5] }
  });
  m.addLayer({
    id: 'import-lines-casing',
    type: 'line',
    source: 'import-lines',
    filter: ['!', ['get', 'original']],
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': '#FFFFFF', 'line-width': ['case', ['get', 'dashed'], 6.5, 8] }
  });
  m.addLayer({
    id: 'import-lines-solid',
    type: 'line',
    source: 'import-lines',
    filter: ['all', ['!', ['get', 'dashed']], ['!', ['get', 'original']]],
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': RED, 'line-width': 5 }
  });
  m.addLayer({
    id: 'import-lines-dashed',
    type: 'line',
    source: 'import-lines',
    filter: ['all', ['get', 'dashed'], ['!', ['get', 'original']]],
    layout: { 'line-join': 'round' },
    paint: { 'line-color': RED, 'line-width': 3.5, 'line-dasharray': [1.5, 1.2] }
  });
  // A bagged-peaks CSV's matches: yellow for a peak the import bags, grey for one already
  // bagged (or a file left out).
  m.addLayer({
    id: 'import-peaks',
    type: 'circle',
    source: 'import-peaks',
    paint: {
      'circle-radius': 6,
      'circle-color': ['case', ['get', 'toBag'], PEAK_TO_BAG, PEAK_BAGGED],
      'circle-stroke-color': '#17181D',
      'circle-stroke-width': 2
    }
  });
  m.addLayer({
    id: 'import-pins',
    type: 'symbol',
    source: 'import-pins',
    layout: { 'icon-image': PIN_ICON, 'icon-size': 0.32, 'icon-allow-overlap': true }
  });
}

// The style's sprite carries the waypoint marker; if a style ever doesn't, draw a plain pin.
function addFallbackPin(m: mapboxgl.Map): void {
  const size = 40;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = RED;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 5, 0, Math.PI * 2);
  ctx.fill();
  m.addImage(PIN_ICON, ctx.getImageData(0, 0, size, size), { pixelRatio: 0.8 });
}

/** Frames the open file, or everything — only when that set changes. The first frame jumps. */
function frame(): void {
  if (!map || !loaded) return;
  const points = cameraPoints(props.model);
  const bounds = boundsOf(points);
  if (!bounds) return;
  const key = JSON.stringify(bounds);
  if (key === framedOn) return;
  const first = framedOn === null;
  framedOn = key;
  map.fitBounds(bounds, { padding: 28, maxZoom: MAX_ZOOM, duration: first ? 0 : FOCUS_EASE_MS });
}

function render(): void {
  if (!map || !loaded) return;
  (map.getSource('import-lines') as mapboxgl.GeoJSONSource | undefined)?.setData(linesData(props.model) as any);
  (map.getSource('import-pins') as mapboxgl.GeoJSONSource | undefined)?.setData(pinsData(props.model) as any);
  (map.getSource('import-peaks') as mapboxgl.GeoJSONSource | undefined)?.setData(peaksData(props.model) as any);
  frame();
}

onMounted(() => {
  if (!container.value) return;
  try {
    mapboxgl.accessToken = MAPBOX_TOKEN;
    map = new mapboxgl.Map({
      container: container.value,
      style: HiikerMapStyle.standard().styleUrl,
      interactive: false,
      attributionControl: false,
      center: [0, 20],
      zoom: 1
    });
    map.addControl(new mapboxgl.AttributionControl({ compact: true }));
    map.on('styleimagemissing', (e: any) => {
      if (e.id === PIN_ICON && map && !map.hasImage(PIN_ICON)) addFallbackPin(map);
    });
    map.on('load', () => {
      if (!map) return;
      loaded = true;
      addLayers(map);
      frame();
    });
    // The dialog lays out around the map (and changes shape on mobile widths).
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => map?.resize());
      resizeObserver.observe(container.value);
    }
  } catch (e) {
    // No WebGL: the dialog still works without its preview.
    console.warn('Import preview map unavailable', e);
  }
});

watch(() => props.model, render);

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  map?.remove();
  map = null;
});
</script>

<style scoped>
.import-map-wrap {
  position: relative;
  width: 100%;
  height: 100%;
}
.import-map-legend {
  position: absolute;
  /* Top-left: the Mapbox logo and attribution sit along the bottom. */
  left: 8px;
  top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(34, 36, 42, 0.88);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  pointer-events: none;
}
.import-map-legend--matching {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.import-map-legend__row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.import-map-legend__swatch {
  width: 22px;
  flex: none;
}
.import-map-legend__swatch--snapped {
  height: 4px;
  border-radius: 2px;
  background: #DB2B35;
  box-shadow: 0 0 0 1.5px #fff;
}
.import-map-legend__swatch--original {
  border-top: 3px dashed #9AA0A6;
}
.import-map-legend__spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffd600;
  border-radius: 50%;
  animation: import-map-legend-spin 0.8s linear infinite;
}
@keyframes import-map-legend-spin {
  to { transform: rotate(360deg); }
}
.import-map {
  width: 100%;
  height: 100%;
  min-height: 160px;
  border-radius: 12px;
  overflow: hidden;
  background: #2b3a2f;
  pointer-events: none;
}
</style>
